import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  GetWasteShipmentQuery,
  ShippingDocumentKind,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useState } from "react";

import { ManifestDrawer } from "../Manifests/ManifestDetailDrawer";
import { ManifestUploadDialog } from "../Manifests/ManifestUploadDialog";
import { ShippingDocumentDrawer } from "../Manifests/ShippingDocumentDrawer";
import { AddBillOfLadingButton } from "../ShippingDocuments/AddBillOfLadingButton";
import {
  ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION,
  ATTACH_SHIPMENT_MANIFEST_MUTATION,
  BILL_OF_LADING_LINK_REFETCH_QUERIES,
  DETACH_SHIPMENT_BILL_OF_LADING_MUTATION,
  DETACH_SHIPMENT_MANIFEST_MUTATION,
  MANIFEST_LINK_REFETCH_QUERIES,
} from "./api";
import { AttachBillOfLadingDialog } from "./AttachBillOfLadingDialog";
import { AttachManifestDialog } from "./AttachManifestDialog";
import { DetachBillOfLadingDialog } from "./DetachBillOfLadingDialog";
import { DetachManifestDialog } from "./DetachManifestDialog";
import { ManifestStepActions } from "./ManifestStepActions";

/**
 * Only the fields this section and its dialogs read, picked from the query
 * result rather than restated: a rename in the query still breaks here, and the
 * prop says what the component actually depends on. Taking the whole shipment
 * left a test no way to build a fixture except casting past the contract.
 */
type Shipment = Pick<
  NonNullable<GetWasteShipmentQuery["wasteShipment"]>,
  "id" | "facilityId" | "status" | "manifestSigned" | "manifest" | "reconciliation"
>;

/**
 * The mutations that change which document the shipment is linked to, with
 * their alerts. They all report failure the same way and none has any bearing
 * on the dialogs' open state, so they sit together and away from it.
 *
 * Detaching dispatches on the attached kind rather than taking a caller's word
 * for it: a manifest and a bill of lading are released by different mutations,
 * and the only reliable answer to "which is attached" is the resolver's own
 * `attachedDocumentKind`.
 */
function useAttachedDocumentActions(shipment: Shipment) {
  const alerts = useAlerts();
  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });
  const [detachManifestMutation, { loading: detachingManifest }] = useMutation(
    DETACH_SHIPMENT_MANIFEST_MUTATION,
    { refetchQueries: MANIFEST_LINK_REFETCH_QUERIES }
  );
  const [detachBillOfLadingMutation, { loading: detachingBol }] = useMutation(
    DETACH_SHIPMENT_BILL_OF_LADING_MUTATION,
    { refetchQueries: BILL_OF_LADING_LINK_REFETCH_QUERIES }
  );

  const setSigned = async (signed: boolean) => {
    const manifestId = shipment.manifest?.id;
    if (!manifestId) {
      return;
    }
    try {
      // Re-attaching the manifest already linked is the signed-copy flow; the
      // server keeps the original received date.
      await attach({
        variables: { input: { shipmentId: shipment.id, processedManifestId: manifestId, signed } },
      });
      alerts.success(signed ? "Marked as the signed copy." : "Marked as an unsigned draft.");
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to update the manifest.");
    }
  };

  /** Resolves true when the link was actually released, so the caller can close. */
  const detachDocument = async (attachedKind: ShippingDocumentKind | null): Promise<boolean> => {
    // Null falls to the manifest branch: a shipment with nothing attached has
    // nothing to detach either way, and the manifest mutation is the one whose
    // no-op the server already tolerates.
    const isManifest = attachedKind == null || attachedKind === ShippingDocumentKind.Manifest;
    try {
      if (isManifest) {
        await detachManifestMutation({ variables: { shipmentId: shipment.id } });
        alerts.success("Manifest detached.");
      } else {
        await detachBillOfLadingMutation({ variables: { shipmentId: shipment.id } });
        alerts.success("Bill of lading detached.");
      }
      return true;
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to detach the document.");
      return false;
    }
  };

  return { attaching, detachingManifest, detachingBol, setSigned, detachDocument };
}

/**
 * Links a bill of lading the reviewer just uploaded and applied to this
 * shipment.
 *
 * The reviewer started from the shipment, so attaching is the intent; making
 * them find the new document in the picker afterwards would be a second step
 * with no decision in it.
 *
 * A failed attach is reported as exactly that. The document is saved either
 * way, and saying only "failed" would read as though the upload was lost and
 * invite a second one.
 */
function useAttachAfterUpload(shipmentId: string) {
  const alerts = useAlerts();
  const [attach] = useMutation(ATTACH_SHIPMENT_BILL_OF_LADING_MUTATION, {
    refetchQueries: BILL_OF_LADING_LINK_REFETCH_QUERIES,
  });

  return async (shippingDocumentId: string) => {
    try {
      // No pre-flight validator call, unlike the picker: the document was
      // created for this facility moments ago, so there is nothing to check it
      // against that the mutation does not check itself.
      await attach({ variables: { input: { shipmentId, shippingDocumentId } } });
      alerts.success("Bill of lading attached.");
    } catch (e) {
      alerts.error(
        e instanceof Error
          ? `Saved the bill of lading, but could not attach it: ${e.message}`
          : "Saved the bill of lading, but could not attach it to this shipment."
      );
    }
  };
}

/** Which detach confirmation to show, given the attached kind. */
function DetachDialog({
  attachedKind,
  trackingNumber,
  documentNumber,
  detachingManifest,
  detachingBol,
  onConfirm,
  onClose,
}: {
  attachedKind: ShippingDocumentKind | null;
  trackingNumber: string | null;
  documentNumber: string | null;
  detachingManifest: boolean;
  detachingBol: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  if (attachedKind != null && attachedKind !== ShippingDocumentKind.Manifest) {
    return (
      <DetachBillOfLadingDialog
        documentNumber={documentNumber}
        detaching={detachingBol}
        onConfirm={onConfirm}
        onClose={onClose}
      />
    );
  }
  return (
    <DetachManifestDialog
      trackingNumber={trackingNumber}
      detaching={detachingManifest}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/**
 * Which dialog is open, and whether the picker it opens is replacing something.
 *
 * Split out because there are four of these and they say nothing about the
 * shipment — keeping them here leaves the component body as the render and the
 * facts it renders from.
 */
function useDocumentDialogState() {
  const [manifestPickerOpen, setManifestPickerOpen] = useState(false);
  const [replacingManifest, setReplacingManifest] = useState(false);
  const [bolPickerOpen, setBolPickerOpen] = useState(false);
  const [replacingBol, setReplacingBol] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bolUploadOpen, setBolUploadOpen] = useState(false);
  const [detachOpen, setDetachOpen] = useState(false);
  // The attached document, read over this shipment rather than in a tab beside
  // it — comparing the two is the whole reason to open it. One flag per kind
  // because they are different drawers reading different records, and a single
  // flag would leave the wrong one mounted for a shipment whose attachment
  // changed while the page was open.
  const [manifestDrawerOpen, setManifestDrawerOpen] = useState(false);
  const [documentDrawerOpen, setDocumentDrawerOpen] = useState(false);

  const openManifestPicker = (replacing: boolean) => {
    setReplacingManifest(replacing);
    setManifestPickerOpen(true);
  };
  const openBolPicker = (replacing: boolean) => {
    setReplacingBol(replacing);
    setBolPickerOpen(true);
  };

  return {
    manifestPickerOpen,
    replacingManifest,
    bolPickerOpen,
    replacingBol,
    uploadOpen,
    bolUploadOpen,
    detachOpen,
    manifestDrawerOpen,
    documentDrawerOpen,
    openManifestPicker,
    openBolPicker,
    closeManifestPicker: () => setManifestPickerOpen(false),
    closeBolPicker: () => setBolPickerOpen(false),
    openUpload: () => setUploadOpen(true),
    closeUpload: () => setUploadOpen(false),
    setBolUploadOpen,
    openDetach: () => setDetachOpen(true),
    closeDetach: () => setDetachOpen(false),
    openManifestDrawer: () => setManifestDrawerOpen(true),
    closeManifestDrawer: () => setManifestDrawerOpen(false),
    openDocumentDrawer: () => setDocumentDrawerOpen(true),
    closeDocumentDrawer: () => setDocumentDrawerOpen(false),
  };
}

/**
 * Every dialog and drawer this section can open, behind one boundary.
 *
 * Split out because the section's own job is the paperwork step — which
 * document is attached, and what can be done to it. Eight conditionally
 * mounted dialogs in the same function made that reading hard to find, and
 * pushed the component past the complexity limit.
 */
function PaperworkDialogs({
  shipment,
  dialogs,
  tenantId,
  attachedKind,
  trackingNumber,
  billOfLadingNumber,
  detachingManifest,
  detachingBol,
  onDetach,
  onSavedBillOfLading,
}: {
  shipment: Shipment;
  dialogs: ReturnType<typeof useDocumentDialogState>;
  tenantId: string | undefined;
  attachedKind: ShippingDocumentKind | null;
  trackingNumber: string | null;
  billOfLadingNumber: string | null;
  detachingManifest: boolean;
  detachingBol: boolean;
  onDetach: () => void;
  onSavedBillOfLading: (savedId: string) => void;
}) {
  const reconciliation = shipment.reconciliation;

  return (
    <>
      <ManifestDrawer
        processedManifestId={shipment.manifest?.id}
        open={dialogs.manifestDrawerOpen}
        onClose={dialogs.closeManifestDrawer}
      />

      {/*
       * The bill of lading's own drawer, the same one the Shipment Documents
       * list opens. Local state rather than the `?shippingDocumentId=` URL
       * param that list uses: this page already opens its manifest drawer from
       * local state, and a second surface writing that param would make the
       * shipment's URL reopen a drawer on reload.
       */}
      <ShippingDocumentDrawer
        shippingDocumentId={reconciliation.linkedShippingDocumentId ?? undefined}
        open={dialogs.documentDrawerOpen}
        onClose={dialogs.closeDocumentDrawer}
      />

      {dialogs.manifestPickerOpen && (
        <AttachManifestDialog
          shipmentId={shipment.id}
          facilityId={shipment.facilityId}
          replacingTrackingNumber={dialogs.replacingManifest ? trackingNumber : null}
          onClose={dialogs.closeManifestPicker}
          // Hand off rather than stack: the picker has served its purpose once
          // the reader knows the manifest is not in it. Replacing is exempt —
          // that swaps in another manifest Encamp already has.
          onUploadInstead={
            dialogs.replacingManifest
              ? undefined
              : () => {
                  dialogs.closeManifestPicker();
                  dialogs.openUpload();
                }
          }
        />
      )}
      {dialogs.bolPickerOpen && (
        <AttachBillOfLadingDialog
          shipmentId={shipment.id}
          facilityId={shipment.facilityId}
          replacingDocumentNumber={dialogs.replacingBol ? billOfLadingNumber : null}
          onClose={dialogs.closeBolPicker}
          // Hand off rather than stack, matching the manifest picker: once the
          // reader knows the document is not on file, this dialog has served
          // its purpose. Replacing is exempt — that swaps in another document
          // Encamp already holds.
          onUploadInstead={
            dialogs.replacingBol
              ? undefined
              : () => {
                  dialogs.closeBolPicker();
                  dialogs.setBolUploadOpen(true);
                }
          }
        />
      )}

      {/*
       * Upload and review, driven from here rather than by its own button:
       * `AddBillOfLadingButton` renders only its dialogs when given `open`, so
       * the picker's handoff above is the trigger. The facility is the
       * shipment's own — the review dialog still shows a facility picker, and
       * moving the document to another facility is what the attach failure
       * message covers.
       */}
      {tenantId && (
        <AddBillOfLadingButton
          tenantId={tenantId}
          facilityId={shipment.facilityId}
          open={dialogs.bolUploadOpen}
          onOpenChange={dialogs.setBolUploadOpen}
          onSaved={onSavedBillOfLading}
        />
      )}
      {dialogs.uploadOpen && tenantId && (
        <ManifestUploadDialog
          open
          onClose={dialogs.closeUpload}
          tenantId={tenantId}
          facilityId={shipment.facilityId}
        />
      )}
      {dialogs.detachOpen && (
        <DetachDialog
          attachedKind={attachedKind}
          trackingNumber={trackingNumber}
          documentNumber={billOfLadingNumber}
          detachingManifest={detachingManifest}
          detachingBol={detachingBol}
          onConfirm={onDetach}
          onClose={dialogs.closeDetach}
        />
      )}
    </>
  );
}

/**
 * The official paper for a shipment — a manifest or a bill of lading — and how
 * it compares to what was requested.
 *
 * Attaching is independent of the shipment's own progress — the vendor's copy
 * can arrive before or after the load leaves — so this section is live in every
 * status except canceled, and never gates or is gated by "Mark shipped".
 *
 * A difference here is not an error. The vendor re-weighs at the scale,
 * re-classifies, splits a line; the attached document is the regulated record
 * and the request was an estimate. So the panel reports rather than blocks, and
 * the severity comes from the same scale the manifest discrepancy views use.
 *
 * Exactly one of a manifest and a bill of lading can be attached at a time, so
 * this component owns both attach flows and picks which dialog to open from
 * `attachedDocumentKind` rather than the caller needing to know.
 */
export function ShipmentManifestSection({
  shipment,
  attachBlockedReason,
}: {
  shipment: Shipment;
  /**
   * Why a document cannot be attached right now, or undefined when it can.
   *
   * Staged container changes are the case: attaching freezes the contents, so
   * the save that was about to commit them would be refused and the edit could
   * only be thrown away. Detaching is deliberately not blocked — it unlocks the
   * contents, so it is the way out rather than a way in.
   */
  attachBlockedReason?: string;
}) {
  const { tenantId } = useTenant();
  const dialogs = useDocumentDialogState();

  const { attaching, detachingManifest, detachingBol, setSigned, detachDocument } =
    useAttachedDocumentActions(shipment);
  const attachAfterUpload = useAttachAfterUpload(shipment.id);

  const reconciliation = shipment.reconciliation;
  const attachedKind = reconciliation.attachedDocumentKind ?? null;
  const isManifestAttached = attachedKind === ShippingDocumentKind.Manifest;
  const isBolAttached = attachedKind != null && !isManifestAttached;

  const trackingNumber =
    reconciliation.linkedManifestTrackingNumber ??
    shipment.manifest?.manifestTrackingNumber ??
    null;
  const billOfLadingNumber = reconciliation.linkedShippingDocumentNumber ?? null;

  // Which drawer the document number opens, or null when there is nothing to
  // open. A bill of lading is always readable once linked; a manifest is not,
  // while its record is still rebuilding.
  let openAttachedDocument: (() => void) | null = null;
  if (isBolAttached && reconciliation.linkedShippingDocumentId) {
    openAttachedDocument = dialogs.openDocumentDrawer;
  } else if (isManifestAttached && shipment.manifest) {
    openAttachedDocument = dialogs.openManifestDrawer;
  }

  const handleDetach = async () => {
    if (await detachDocument(attachedKind)) {
      dialogs.closeDetach();
    }
  };

  // A canceled shipment can take no paperwork, and its step already renders
  // inactive and greyed with nothing under it — which reads as "not applicable"
  // without a sentence saying so.
  if (shipment.status === WasteShipmentStatus.Canceled) {
    return null;
  }

  return (
    <>
      <ManifestStepActions
        attachedKind={attachedKind}
        documentNumber={isManifestAttached ? trackingNumber : billOfLadingNumber}
        // Either kind opens in a drawer over this page, because the reason to
        // open the paperwork is to read it against the shipment — a route that
        // navigates away costs the reader the thing they are comparing it to.
        // Null while a manifest link dangles: there is a tracking number from
        // the snapshot but no manifest row to open yet.
        onOpenDocument={openAttachedDocument}
        signed={shipment.manifestSigned}
        dangling={isManifestAttached && shipment.manifest == null}
        attachBlockedReason={attaching ? "Attaching…" : attachBlockedReason}
        signedChangePending={attaching}
        onAttachManifest={() => dialogs.openManifestPicker(isManifestAttached)}
        onAttachBillOfLading={() => dialogs.openBolPicker(isBolAttached)}
        onRelink={() =>
          isBolAttached ? dialogs.openBolPicker(true) : dialogs.openManifestPicker(true)
        }
        onDetach={dialogs.openDetach}
        onSignedChange={setSigned}
      />

      <PaperworkDialogs
        shipment={shipment}
        dialogs={dialogs}
        tenantId={tenantId}
        attachedKind={attachedKind}
        trackingNumber={trackingNumber}
        billOfLadingNumber={billOfLadingNumber}
        detachingManifest={detachingManifest}
        detachingBol={detachingBol}
        onDetach={handleDetach}
        onSavedBillOfLading={attachAfterUpload}
      />
    </>
  );
}
